/**
 * The product page's "Inputs" panel (a sold product: its inputs, what we last paid each, "Add input") and
 * "Usually becomes" panel (an input: the sold products, "Add") (TASK-0259, FRESH-1). The links are
 * navigation only: nothing here writes cost, stock or a sale, and "inputs last paid" is information. The
 * picker follows the owner's 6-month rule (features/catalog/families.ts).
 */
import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api/client';
import { dayLabel, money } from '@/lib/format';
import type {
  ProductBecomesLink,
  ProductInputLink,
  ProductSummary,
} from '@/lib/api/types';
import { Badge, Code } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Input } from '@/ui/Field';
import { Panel } from '@/ui/Panel';
import { pickerRowLabel, useLinkCandidates, type PickerSide } from './families';

/** "$4.79 / lb · Stonehill 101 · Oct 14", or what is known of it. */
export function lastPaidLine(input: ProductInputLink): string {
  const parts: string[] = [];
  if (input.lastPaidUnitCostCents != null) {
    parts.push(
      `${money(input.lastPaidUnitCostCents)} / ${input.unit === 'lb' ? 'lb' : 'ea'}`
    );
  } else if (input.lastPaidPackCostCents != null) {
    parts.push(`${money(input.lastPaidPackCostCents)} a pack`);
  }
  const vendor = [input.vendorName, input.vendorSku].filter(Boolean).join(' ');
  if (vendor) parts.push(vendor);
  if (input.lastPaidOn) parts.push(dayLabel(input.lastPaidOn));
  return parts.length > 0 ? parts.join(' · ') : 'not bought yet';
}

/** The one-line summary under the cost field: every input's last paid, information only. */
export function inputsLastPaidSummary(inputs: ProductInputLink[]): string | null {
  const paid = inputs.filter(
    (i) => i.lastPaidUnitCostCents != null || i.lastPaidPackCostCents != null
  );
  if (paid.length === 0) return null;
  return paid.map(lastPaidLine).join('; ');
}

export function LinkPicker({
  side,
  family,
  excludeIds,
  onPick,
  autoFocus,
}: {
  side: PickerSide;
  family: string | null | undefined;
  excludeIds: Set<number>;
  onPick: (row: ProductSummary) => void;
  autoFocus?: boolean;
}) {
  const [term, setTerm] = useState('');
  const [older, setOlder] = useState(false);
  const listId = useId();
  const q = useLinkCandidates({ side, term, older, family });
  const rows = (q.data?.rows ?? []).filter((r) => !excludeIds.has(Number(r.id)));
  const showList = term.trim().length >= 2;
  return (
    <div>
      <Input
        size="sm"
        value={term}
        autoFocus={autoFocus}
        prefix={<Search />}
        role="combobox"
        aria-label={side === 'inputs' ? 'Search inputs' : 'Search sold products'}
        aria-expanded={showList}
        aria-controls={showList ? listId : undefined}
        autoComplete="off"
        placeholder={
          side === 'inputs'
            ? 'Find an input product by name…'
            : 'Find the sold product by name or barcode…'
        }
        onChange={(e) => setTerm(e.currentTarget.value)}
      />
      <p className="text-2xs text-ink-faint mt-1 flex items-center gap-2">
        {older
          ? 'Showing every active product.'
          : side === 'inputs'
            ? 'Inputs bought in the last 6 months, same family first.'
            : 'Products sold or bought in the last 6 months, same family first.'}
        {!older ? (
          <button
            type="button"
            className="text-signal-ink hover:underline"
            onClick={() => setOlder(true)}
          >
            Show older items
          </button>
        ) : null}
      </p>
      {showList ? (
        <ul
          id={listId}
          role="listbox"
          className="border-line mt-1 max-h-56 overflow-y-auto rounded border"
        >
          {q.isPending ? (
            <li className="text-2xs text-ink-faint px-2 py-2">Searching…</li>
          ) : q.isError ? (
            <li className="text-2xs text-negative px-2 py-2">{errorMessage(q.error)}</li>
          ) : rows.length === 0 ? (
            <li className="text-2xs text-ink-faint px-2 py-2">
              {older
                ? 'Nothing matches.'
                : 'Nothing bought or sold recently matches; try "Show older items".'}
            </li>
          ) : (
            rows.map((row) => (
              <li key={row.id} role="option" aria-selected={false}>
                <button
                  type="button"
                  className="hover:bg-surface-hover flex w-full items-center gap-2 px-2 py-1 text-left text-xs"
                  onClick={() => {
                    onPick(row);
                    setTerm('');
                  }}
                >
                  <span className="text-ink min-w-0 flex-1 truncate">{row.name}</span>
                  {row.productionFamily === family && family ? (
                    <Badge tone="info">Same family</Badge>
                  ) : null}
                  <span className="text-2xs text-ink-faint truncate">
                    {pickerRowLabel(row)}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

export function InputsPanel({
  productId,
  family,
  inputs,
}: {
  productId: number;
  family: string | null | undefined;
  inputs: ProductInputLink[];
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const save = useMutation({
    mutationFn: (inputProductIds: number[]) =>
      api.put(`/products/${productId}/inputs`, { inputProductIds }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('Inputs saved');
    },
    onError: (err) =>
      toast.error('Could not save the inputs', { description: errorMessage(err) }),
  });
  const ids = inputs.map((i) => i.productId);
  const byProduct = new Map<number, ProductInputLink[]>();
  for (const i of inputs)
    byProduct.set(i.productId, [...(byProduct.get(i.productId) ?? []), i]);
  return (
    <Panel
      title="Inputs"
      description="What this is usually made from. For finding things and for the in / out report, never for cost or stock."
      actions={
        <Button
          size="xs"
          icon={<Plus className="size-3" />}
          onClick={() => setAdding((a) => !a)}
        >
          {adding ? 'Close' : 'Add input'}
        </Button>
      }
    >
      {adding ? (
        <div className="mb-3">
          <LinkPicker
            side="inputs"
            family={family}
            excludeIds={new Set([...ids, productId])}
            autoFocus
            onPick={(row) => save.mutate([...new Set([...ids, Number(row.id)])])}
          />
        </div>
      ) : null}
      {byProduct.size === 0 ? (
        <p className="text-2xs text-ink-faint">No input linked yet.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="product-inputs">
          {[...byProduct.entries()].map(([id, links]) => (
            <li key={id} className="flex items-start gap-2 text-xs">
              <div className="min-w-0 flex-1">
                <Link
                  to="/products/$id"
                  params={{ id: String(id) }}
                  className="text-ink hover:underline"
                >
                  {links[0]!.name}
                </Link>
                {!links[0]!.active ? (
                  <Badge tone="warn" className="ml-1.5">
                    Inactive
                  </Badge>
                ) : null}
                <div className="text-2xs text-ink-muted">
                  {links.map((l) => (
                    <div key={`${l.vendorId ?? 'none'}`}>
                      Last paid {lastPaidLine(l)}
                      {l.lastBoughtOn ? ` · last bought ${dayLabel(l.lastBoughtOn)}` : ''}
                    </div>
                  ))}
                </div>
              </div>
              <Button
                size="icon-sm"
                variant="ghost"
                title="Remove this input"
                aria-label={`Remove input ${links[0]!.name}`}
                loading={save.isPending}
                onClick={() => save.mutate(ids.filter((x) => x !== id))}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function BecomesPanel({
  productId,
  family,
  becomes,
}: {
  productId: number;
  family: string | null | undefined;
  becomes: ProductBecomesLink[];
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const save = useMutation({
    mutationFn: (productIds: number[]) =>
      api.put(`/products/${productId}/becomes`, { productIds }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('Saved what this input becomes');
    },
    onError: (err) => toast.error('Could not save', { description: errorMessage(err) }),
  });
  const ids = becomes.map((b) => b.productId);
  return (
    <Panel
      title="Usually becomes"
      description="The sold products this input usually turns into. A hint for finding things; it moves no stock and sets no cost."
      actions={
        <Button
          size="xs"
          icon={<Plus className="size-3" />}
          onClick={() => setAdding((a) => !a)}
        >
          {adding ? 'Close' : 'Add'}
        </Button>
      }
    >
      {adding ? (
        <div className="mb-3">
          <LinkPicker
            side="becomes"
            family={family}
            excludeIds={new Set([...ids, productId])}
            autoFocus
            onPick={(row) => save.mutate([...new Set([...ids, Number(row.id)])])}
          />
        </div>
      ) : null}
      {becomes.length === 0 ? (
        <p className="text-2xs text-ink-faint">Nothing linked yet.</p>
      ) : (
        <ul className="space-y-1.5" data-testid="product-becomes">
          {becomes.map((b) => (
            <li key={b.productId} className="flex items-center gap-2 text-xs">
              <Link
                to="/products/$id"
                params={{ id: String(b.productId) }}
                className="text-ink min-w-0 flex-1 truncate hover:underline"
              >
                {b.name}
              </Link>
              {!b.active ? <Badge tone="warn">Inactive</Badge> : null}
              <span className="text-2xs text-ink-faint">
                {b.soldLastOn ? `last sold ${dayLabel(b.soldLastOn)}` : 'not sold yet'}
              </span>
              <Code>{String(b.productId)}</Code>
              <Button
                size="icon-sm"
                variant="ghost"
                title="Remove"
                aria-label={`Remove ${b.name}`}
                loading={save.isPending}
                onClick={() => save.mutate(ids.filter((x) => x !== b.productId))}
              >
                <X className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
